import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Package, 
  FileText,
  Sparkles,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

export const Home = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const logoUrl = theme === 'light'
    ? 'https://customer-assets.emergentagent.com/job_stockwise-app-17/artifacts/wbwkqvrv_Claro.png'
    : 'https://customer-assets.emergentagent.com/job_stockwise-app-17/artifacts/waabplrj_Claro.png';

  const features = [
    {
      icon: BarChart3,
      title: 'Dashboard Inteligente',
      description: 'Visualize seus dados com gráficos customizáveis e insights em tempo real'
    },
    {
      icon: Sparkles,
      title: 'Assistente IA',
      description: 'Faça perguntas sobre seu negócio e receba análises instantâneas'
    },
    {
      icon: Package,
      title: 'Gestão de Estoque',
      description: 'Controle completo de produtos com alertas automáticos'
    },
    {
      icon: Users,
      title: 'Clientes & Vendedores',
      description: 'Gerencie relacionamentos e acompanhe comissões'
    },
    {
      icon: FileText,
      title: 'Romaneios Profissionais',
      description: 'Gere PDFs elegantes com todas as informações necessárias'
    },
    {
      icon: TrendingUp,
      title: 'Análises Avançadas',
      description: 'Tome decisões baseadas em dados concretos'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5" data-testid="home-page">
      {/* Hero Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="text-center mb-16">
          <img 
            src={logoUrl} 
            alt="Distribuidora Santa Ana" 
            className="h-32 w-auto mx-auto mb-8 object-contain"
          />
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-foreground mb-6">
            Bem-vinda ao seu
            <span className="text-primary block mt-2">Sistema de Gestão</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Controle total da sua distribuidora com inteligência artificial e análises em tempo real
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="go-to-dashboard-btn"
            className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-3 group"
          >
            Acessar Dashboard
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
              >
                <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2 text-foreground">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold mb-3">
              Seu Negócio em Números
            </h2>
            <p className="text-white/80">
              Dados precisos para decisões inteligentes
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-white/80">Controle</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">IA</div>
              <div className="text-white/80">Insights</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">Real-Time</div>
              <div className="text-white/80">Dados</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-white/80">Acesso</div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-card border border-border rounded-2xl p-12">
          <Sparkles className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            Pronta para começar?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Acesse seu dashboard personalizado e comece a gerenciar seu negócio de forma inteligente
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-3"
          >
            Ir para o Dashboard
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
